import { useFormatDate, useOpen } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TravelInquiryEditDrawer } from "../../../../../../shared/TravelInquiryDrawer";
import {
	GuestTravelInquiry,
	GuestTravelInquiryFlight,
	GuestTravelInquiryTrain,
} from "../../../../../../shared/types/travelInquiry";
import { CardBody, CardHeader, TravelCard } from "./Card";
import { LabeledValue } from "./LabeledValue";
import { Leg, LegDestination } from "./Leg";

interface Props<T extends GuestTravelInquiry> {
	travelInquiry: T;
	onRemove: () => void;
	handleEditInquiry: (inquiry: GuestTravelInquiry) => void;
}

const TravelDates = styled.div`
	display: flex;
	justify-content: space-between;
	padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const TravelInquiryCard: FC<Props<GuestTravelInquiry>> = ({ travelInquiry, ...props }) => {
	switch (travelInquiry.type) {
		case "train":
			return <TravelInquiryTrainCard travelInquiry={travelInquiry} {...props} />;
		case "flight":
			return <TravelInquiryFlightCard travelInquiry={travelInquiry} {...props} />;
	}
};

const TravelInquiryFlightCard: FC<Props<GuestTravelInquiryFlight>> = ({
	travelInquiry,
	onRemove,
	handleEditInquiry,
}) => {
	const { t } = useTranslation();
	const formatDate = useFormatDate();
	const { open, handleOpen, handleClose } = useOpen();

	return (
		<>
			<TravelCard>
				<CardHeader onRemove={onRemove} onEdit={handleOpen}>
					{t("Inquiry")}
				</CardHeader>
				<CardBody>
					<Leg
						iconSrc="/icons/travel.svg"
						departure={
							<LegDestination
								alignment="left"
								name={`(${travelInquiry.departureLocation?.iataCode}) ${travelInquiry.departureLocation?.name}`}
								time={travelInquiry.earliestDepartureTime}
							/>
						}
						arrival={
							<LegDestination
								alignment="right"
								name={`(${travelInquiry.arrivalLocation?.iataCode}) ${travelInquiry.arrivalLocation?.name}`}
								time={travelInquiry.latestArrivalTime}
							/>
						}
					/>
				</CardBody>
				<TravelDates>
					<LabeledValue label={t("Earliest departure")}>
						<span>
							{formatDate(travelInquiry.earliestDepartureDate)}, {travelInquiry.earliestDepartureTime}
						</span>
					</LabeledValue>
					<LabeledValue label={t("Latest arrival")}>
						<span>
							{formatDate(travelInquiry.latestArrivalDate)}, {travelInquiry.latestArrivalTime}
						</span>
					</LabeledValue>
				</TravelDates>
				{travelInquiry.remarks && <LabeledValue label={t("Remarks")}>{travelInquiry.remarks}</LabeledValue>}
			</TravelCard>
			<TravelInquiryEditDrawer
				open={open}
				travelInquiry={travelInquiry}
				onSubmit={handleEditInquiry}
				handleClose={handleClose}
			/>
		</>
	);
};

const TravelInquiryTrainCard: FC<Props<GuestTravelInquiryTrain>> = ({ travelInquiry, onRemove, handleEditInquiry }) => {
	const { t } = useTranslation();
	const formatDate = useFormatDate();
	const { open, handleOpen, handleClose } = useOpen();

	return (
		<>
			<TravelCard>
				<CardHeader onRemove={onRemove} onEdit={handleOpen}>
					{t("Inquiry")}
				</CardHeader>
				<CardBody>
					<Leg
						iconSrc="/icons/train.svg"
						departure={
							<LegDestination
								alignment="left"
								name={travelInquiry.departureLocation}
								time={travelInquiry.earliestDepartureTime}
							/>
						}
						arrival={
							<LegDestination
								alignment="right"
								name={travelInquiry.arrivalLocation}
								time={travelInquiry.latestArrivalTime}
							/>
						}
					/>
				</CardBody>
				<TravelDates>
					<LabeledValue label={t("Earliest departure")}>
						<span>
							{formatDate(travelInquiry.earliestDepartureDate)}, {travelInquiry.earliestDepartureTime}
						</span>
					</LabeledValue>
					<LabeledValue label={t("Latest arrival")}>
						<span>
							{formatDate(travelInquiry.latestArrivalDate)}, {travelInquiry.latestArrivalTime}
						</span>
					</LabeledValue>
				</TravelDates>
				{travelInquiry.remarks && <LabeledValue label={t("Remarks")}>{travelInquiry.remarks}</LabeledValue>}
			</TravelCard>
			<TravelInquiryEditDrawer
				open={open}
				travelInquiry={travelInquiry}
				onSubmit={handleEditInquiry}
				handleClose={handleClose}
			/>
		</>
	);
};
